'use client';

import {
    useQuery,
    useQueryClient,
    keepPreviousData,
    type UseQueryResult,
    type QueryKey,
    type QueryFunction,
} from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getCached, setCached } from './cacheUtils';
import { useAuth } from '@/providers/AuthContext';

// ── Global sync toast ──
const SYNC_TOAST_ID = 'cached-query-sync';
let activeFetches = 0;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let dismissTimer: ReturnType<typeof setTimeout> | null = null;
let toastVisible = false;

function trackFetchStart() {
    activeFetches++;
    if (dismissTimer) {
        clearTimeout(dismissTimer);
        dismissTimer = null;
    }
    // Only show toast if fetch takes > 700ms
    if (!showTimer && !toastVisible) {
        showTimer = setTimeout(() => {
            showTimer = null;
            toastVisible = true;
            toast.loading('Dados desatualizados, atualizando…', { id: SYNC_TOAST_ID });
        }, 700);
    }
}

function trackFetchEnd() {
    activeFetches = Math.max(0, activeFetches - 1);
    if (activeFetches === 0) {
        // Cancel pending show timer if fetch finished fast
        if (showTimer) {
            clearTimeout(showTimer);
            showTimer = null;
        }
        if (toastVisible) {
            dismissTimer = setTimeout(() => {
                dismissTimer = null;
                toastVisible = false;
                toast.success('Dados atualizados', { id: SYNC_TOAST_ID, duration: 2000 });
            }, 50);
        }
    }
}

export interface UseCachedQueryOptions<TData> {
    queryKey: QueryKey;
    queryFn: QueryFunction<TData>;
    enabled?: boolean;
    cacheTtlMs?: number;
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number;
    /** Show global sync toast on background refetch. Default: false */
    notify?: boolean;
    /** Number of retries on failure. Default: 3 (TanStack default) */
    retry?: number | false;
    /** Delay between retries in ms */
    retryDelay?: number;
    /**
     * 'always' refaz o fetch ao montar mesmo com cache fresco (staleTime).
     * Use em snapshots mantidos por socket: desmontado, o consumidor sai da
     * room e perde os deltas — o cache nunca é confiável no remount.
     * Default: TanStack (true — refetch só se stale).
     */
    refetchOnMount?: boolean | 'always';
}

export type CachedQueryResult<TData> = UseQueryResult<TData> & {
    showLoading: boolean;
    /** True while the displayed data came from IndexedDB and network hasn't resolved yet */
    isFromCache: boolean;
};

export function useCachedQuery<TData>({
    queryKey,
    queryFn,
    enabled = true,
    cacheTtlMs,
    refetchOnWindowFocus = false,
    refetchInterval = 1000 * 60 * 5,
    notify = false,
    retry,
    retryDelay,
    refetchOnMount,
}: UseCachedQueryOptions<TData>): CachedQueryResult<TData> {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const userId = user?.user_id ? String(user.user_id) : null;
    const queryKeyStr = JSON.stringify(queryKey);
    const isTracking = useRef(false);
    const hydratedFromCache = useRef(false);

    // ── Hydrate: IndexedDB → TanStack cache (parallel with query) ──
    // Runs alongside the network fetch. If IndexedDB resolves first, user sees
    // cached data immediately. The guard `!queryClient.getQueryData(queryKey)`
    // ensures we never overwrite fresher network data with stale cache.
    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        getCached<TData>(queryKey as readonly unknown[], userId, cacheTtlMs).then((cached) => {
            if (cancelled) return;
            if (cached !== null && !queryClient.getQueryData(queryKey)) {
                queryClient.setQueryData(queryKey, cached);
                hydratedFromCache.current = true;
            }
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, queryKeyStr]);

    // ── Query (runs in parallel — NOT gated on hydration) ──
    const result = useQuery<TData, Error, TData, QueryKey>({
        queryKey,
        queryFn,
        enabled,
        staleTime: 60_000,
        refetchOnWindowFocus,
        refetchInterval,
        placeholderData: keepPreviousData,
        networkMode: 'offlineFirst',
        ...(retry !== undefined && { retry }),
        ...(retryDelay !== undefined && { retryDelay }),
        ...(refetchOnMount !== undefined && { refetchOnMount }),
    });

    // ── Persist fresh network data to IndexedDB ──
    useEffect(() => {
        if (!userId || !result.data || result.isFetching || result.isPlaceholderData) return;
        hydratedFromCache.current = false;
        void setCached(queryKey as readonly unknown[], result.data, userId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, result.data, result.isFetching, result.isPlaceholderData]);

    // ── Toast (only when notify: true) ──
    const hasData = !!result.data;
    const isFetching = result.isFetching;
    const dataUpdatedAt = result.dataUpdatedAt;

    useEffect(() => {
        if (!notify) return;
        if (isFetching && hasData && !isTracking.current) {
            isTracking.current = true;
            trackFetchStart();
        }
        // Only dismiss when fresh data actually arrived (dataUpdatedAt changed)
        if (!isFetching && isTracking.current && !hydratedFromCache.current) {
            isTracking.current = false;
            trackFetchEnd();
        }
    }, [isFetching, hasData, notify, dataUpdatedAt]);

    useEffect(() => {
        return () => {
            if (isTracking.current) {
                isTracking.current = false;
                trackFetchEnd();
            }
        };
    }, []);

    const showLoading = !result.data && result.isFetching;
    const isFromCache = hydratedFromCache.current;
    return { ...result, showLoading, isFromCache } as CachedQueryResult<TData>;
}
